import isEqual from 'lodash.isequal';
import cloneDeep from 'lodash.clonedeep';
import { mapAddress } from '@general/js/utils/JoinNow';
import { OperationType, NonDependantFlexCodes } from '@/enums';
import { CoverTypeValue, NeedsAnalysisEligibility, LifetimeHealthCoverSituation } from '@general/js/enums';
import { showConcessionCardSection as showConcessionCard, showConcessionCardDetailsForm } from '@/utils';
import { isEmpty } from 'lodash';
import { mapLhcPayload } from '../GetPremiumPrice/PersonUtils';
import { mapVeteranCardPayload, mapConcessionCardPayload } from '../ChangeCoverUtils';

// eslint-disable-next-line import/prefer-default-export
export const mapPolicyHolderObj = (initialPolicyHolder, currentPolicyHolder, changeCoverData, existingMemberDetails) => {
  const initialPostal = mapAddress(initialPolicyHolder.postalAddress, initialPolicyHolder.differentPostalAddress, initialPolicyHolder.POBox);
  let currentPostal = mapAddress(currentPolicyHolder.postalAddress, currentPolicyHolder.differentPostalAddress, currentPolicyHolder.POBox);

  // if had postal address and still has it, but details have changed, operation is REPLACE
  // if did not have postal address before and does have it now, operation is ADD
  // if did not have postal address before and does not have it now, operation is NONE
  // if had postal and still has it, but details did not change, operation is NONE
  let postalOp = OperationType.NONE;
  if (initialPostal?.addressLine1 && !isEqual(initialPostal, currentPostal)) {
    postalOp = OperationType.REPLACE;
  } else if (!initialPostal?.addressLine1) {
    postalOp = OperationType.ADD;
  }

  const noLongerEligibleForVeteran =
    initialPolicyHolder.veteranCard.haveVeteranCard &&
    (changeCoverData.step1.eligibility === NeedsAnalysisEligibility.WORK_CONNECTION || changeCoverData.step1.eligibility === NeedsAnalysisEligibility.RELATED);

  const veteranCardObj = {
    hasVeteranCard: noLongerEligibleForVeteran ? false : currentPolicyHolder.veteranCard.haveVeteranCard,
    veteranCardList: mapVeteranCardPayload(initialPolicyHolder.veteranCard, currentPolicyHolder.veteranCard, noLongerEligibleForVeteran),
  };

  // if had initial concession cards, and no longer eligible for concession, remove all cards in list
  const addressState = changeCoverData.step1.state;
  const coverType = changeCoverData.step3.coverType;
  const extrasOnly = coverType === CoverTypeValue.EXTRAS_ONLY;
  const policyHolderDOB = changeCoverData.step1.DOB;
  const noLongerEligibleForConcession = initialPolicyHolder.haveConcessionCard && !showConcessionCard(addressState, policyHolderDOB, extrasOnly);

  const concessionCardObj = {
    hasConcessionCard: noLongerEligibleForConcession ? false : currentPolicyHolder.haveConcessionCard,
    cardList: mapConcessionCardPayload(
      initialPolicyHolder.haveConcessionCard,
      initialPolicyHolder.selectedConcessionCardOptions,
      currentPolicyHolder.haveConcessionCard,
      currentPolicyHolder.selectedConcessionCardOptions,
      noLongerEligibleForConcession,
      showConcessionCardDetailsForm(addressState, policyHolderDOB, extrasOnly, currentPolicyHolder.haveConcessionCard),
    ),
  };

  if (currentPostal === null) {
    currentPostal = currentPolicyHolder.residentialAddress; // if no postal address, take the residential address
  }

  const mainPolicyHolder = {
    op: OperationType.NONE,
    personCode: currentPolicyHolder.personCode,
    postalAddress: {
      op: postalOp,
      ...currentPostal,
    },
    veteranCard: currentPolicyHolder.veteranCard.haveVeteranCard !== null ? veteranCardObj : null,
    concessionCard: currentPolicyHolder.haveConcessionCard !== null ? concessionCardObj : null,
  };

  // Policy Holder Postal address (if changed)
  if (postalOp === OperationType.REPLACE || postalOp === OperationType.ADD) {
    mainPolicyHolder.op = OperationType.REPLACE;
  }

  const initialAbd = !isEmpty(initialPolicyHolder.abd) ? initialPolicyHolder.abd : null;
  const currentAbd = !isEmpty(currentPolicyHolder.abd) ? currentPolicyHolder.abd : null;
  const initialPortalAbd = !isEmpty(initialPolicyHolder.portalAgeBasedDiscount) ? initialPolicyHolder.portalAgeBasedDiscount : null;
  // Only send abd details if being added for the first time and no existing portalAgeBasedDiscount record
  if (initialAbd === null && currentAbd !== null && currentAbd.cda !== null && !initialPortalAbd) {
    mainPolicyHolder.op = OperationType.REPLACE;
    mainPolicyHolder.cda = currentAbd.cda;
  }

  const initialLhc = !isEmpty(initialPolicyHolder.lhc) ? mapLhcPayload(initialPolicyHolder.lhc) : null;
  const currentLhc = !isEmpty(currentPolicyHolder.lhc) ? mapLhcPayload(currentPolicyHolder.lhc) : null;

  // If there is existing LHCOverrule for policyholder, do not send LHC object
  const existingLhcOverrule = existingMemberDetails?.lhcOverrule !== null;

  if (!existingLhcOverrule) {
    // Only send lhc details if being added for the first time - check active lhc details
    if (initialLhc === null && currentLhc !== null && currentLhc.overruledReason?.toLowerCase() !== LifetimeHealthCoverSituation.NONE.toLowerCase()) {
      mainPolicyHolder.op = OperationType.REPLACE;
      mainPolicyHolder.lifetimeHealthCoverOverrule = currentLhc;
    }
  }

  return mainPolicyHolder;
};

export const mapOtherPolicyUpdates = (memberPolicies, address, initialAddress) => {
  const addressObj = cloneDeep(address);
  const initialAddressObj = cloneDeep(initialAddress);
  let otherPolicyUpdatesObj = null;

  // If address or initial address or member policies is null, return null
  if (addressObj === null || initialAddressObj === null || memberPolicies === null) {
    return otherPolicyUpdatesObj;
  }

  // If there is no changes to address postcode or state, return null
  if (addressObj.postcode === initialAddressObj.postcode && addressObj.state === initialAddressObj.state) {
    return otherPolicyUpdatesObj;
  }

  // Filter memberships where the relationship is Policy Holder
  const policyHolderMemberships = memberPolicies.filter((membership) => {
    return membership.relationship === NonDependantFlexCodes.POLICY_HOLDER;
  });

  // If there is only one membership where the relationship is policy holder, no changes required
  if (policyHolderMemberships.length === 1) {
    return otherPolicyUpdatesObj;
  }

  // If there is more than one membership and the member is a policy holder on the memberships
  // Update the other policies' postcode or state
  if (policyHolderMemberships.length > 1) {
    otherPolicyUpdatesObj = {
      memberships: policyHolderMemberships.map((membership) => {
        return {
          memberNumber: membership.memberNumber,
          postcode: addressObj.postcode !== membership.postcode ? addressObj.postcode : null,
          state: addressObj.state !== membership.state ? addressObj.state : null,
        };
      }),
    };
  }

  return otherPolicyUpdatesObj;
};
